import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";

/** Indian mobile: exactly 10 digits starting with 6–9 */
const INDIA_MOBILE_REGEX = /^[6-9]\d{9}$/;
const INDIA_MOBILE_LENGTH = 10;

function isIndiaCallingCode(parsed) {
  return (
    parsed?.country === "IN" ||
    parsed?.countryCallingCode === "91"
  );
}

function isIndiaSelection(value, country) {
  // The value's own calling code wins over `country`: the caller's country
  // state lags a country change by one render, and trusting it there would
  // rewrite a number the user just switched to another country back to +91.
  if (value?.startsWith("+")) return value.startsWith("+91");
  if (country === "IN") return true;
  return false;
}

export function isValidIndianMobile(nationalNumber) {
  return INDIA_MOBILE_REGEX.test(nationalNumber || "");
}

/**
 * Limit +91 / IN input while typing: drop invalid leading digits and cap at 10.
 * Leaves other country codes unchanged.
 */
export function restrictPhoneInput(value, country) {
  if (!value) return value;
  if (!isIndiaSelection(value, country)) return value;

  let national = value.startsWith("+91")
    ? value.slice(3).replace(/\D/g, "")
    : value.replace(/\D/g, "");

  // If value was full E.164 for another format, prefer parsed national digits
  if (!value.startsWith("+91")) {
    try {
      const parsed = parsePhoneNumber(value, "IN");
      if (parsed?.nationalNumber) {
        national = parsed.nationalNumber;
      }
    } catch {
      // keep digit-stripped national
    }
  }

  national = national.replace(/^[0-5]+/, "");
  if (national.length > INDIA_MOBILE_LENGTH) {
    national = national.slice(0, INDIA_MOBILE_LENGTH);
  }

  return national ? `+91${national}` : "+91";
}

/** National digit count for an India E.164 value (for input guards). */
export function getIndiaNationalDigitCount(value) {
  if (!value?.startsWith("+91")) return 0;
  return value.slice(3).replace(/\D/g, "").length;
}

/**
 * Form-friendly phone check: accept numbers with a correct length/format for
 * the country. Strict `isValidPhoneNumber` rejects unallocated area codes
 * (e.g. DO +1 764…) even when the input is complete and correctly shaped.
 * India (+91) is restricted to valid 10-digit mobiles starting with 6–9.
 */
export function isPhoneNumberAcceptable(value) {
  if (!value) return false;
  try {
    const parsed = parsePhoneNumber(value);
    if (parsed && isIndiaCallingCode(parsed)) {
      return isValidIndianMobile(parsed.nationalNumber);
    }
    // Incomplete +91 (too few digits) may not parse — still reject non-mobiles
    if (value.startsWith("+91")) {
      const national = value.slice(3).replace(/\D/g, "");
      if (!national) return false;
      return isValidIndianMobile(national);
    }
    return isPossiblePhoneNumber(value) || isValidPhoneNumber(value);
  } catch {
    return false;
  }
}

/**
 * Normalize PhoneInput onChange value into API fields + validation flag.
 * Always validates the E.164 value from the input — never re-concatenate
 * countryCode + nationalNumber (that breaks when `phone` still holds E.164).
 */
export function parsePhoneInput(value) {
  const e164 = value || "";
  if (!e164 || !isPhoneNumberAcceptable(e164)) {
    return {
      isValid: false,
      e164,
      countryCode: "",
      nationalNumber: "",
      country: undefined,
    };
  }

  try {
    const parsed = parsePhoneNumber(e164);
    if (!parsed?.countryCallingCode || !parsed?.nationalNumber) {
      return {
        isValid: false,
        e164,
        countryCode: "",
        nationalNumber: "",
        country: undefined,
      };
    }

    return {
      isValid: true,
      e164: parsed.number || e164,
      countryCode: `+${parsed.countryCallingCode}`,
      nationalNumber: parsed.nationalNumber,
      country: parsed.country,
    };
  } catch {
    return {
      isValid: false,
      e164,
      countryCode: "",
      nationalNumber: "",
      country: undefined,
    };
  }
}
